/**
 * Simple i18n composable for SSi Learning App
 *
 * Uses static JSON locale files, keyed by ISO 639-3 language codes.
 * Falls back to English if translation not found.
 */

import { ref, shallowRef, computed, type Ref } from 'vue'

// Only `eng` is eager — it's the universal fallback (used by t() when a
// key is missing in the current locale), so it needs to be available
// synchronously at module load. Every other locale is lazy-loaded via
// import() so a single-language user (which is most users) doesn't pay
// for ~230KB of locale JSON on first paint. See setLocale() below.
// File names use ISO 639-3 codes (3-char).
//
// And only the LEARNER part of `eng` is eager (job #850): the schools, org,
// teach and insight namespaces and the compiled walkthrough/explainer mirrors
// — three quarters of the file — come in a second chunk, loadDeferredEnglish()
// below. The split is scripts/engLocaleSplit.mjs; eng.json itself stays whole.
import engBoot from 'virtual:eng-boot'
import { variantCountryCode, extractBaseLanguage } from '@/utils/variantFlag'

type LocaleLoader = () => Promise<Record<string, any>>

const unwrap = (m: any): Record<string, any> => (m && m.default) || m

// THE LOCALE CHUNKS ARE NAMED ELSEWHERE (job #966). Each non-English locale is
// its own lazy chunk, and an import() names its target by hashed file name — so
// a module that holds those import()s is renamed whenever any locale changes,
// and so is every chunk that imports it. 156 modules import this one. The
// import()s therefore live in src/i18n/localeLoaders.ts, which only the entry
// imports, and it hands them in through registerLocaleLoaders() below. This
// file names no chunk, and the chunks that use t() stop being renamed with
// every locale or route change. See scripts/stableSharedChunk.mjs.
//
// The codes are listed here as well because isSupportedLocale() must answer
// before the loaders arrive; localeLoaders.test.ts holds the two lists equal.
export const LOCALE_CODES = [
  'eng', 'gle', 'ga', 'spa', 'cym', 'cym_n', 'cym_s', 'ara', 'deu', 'fra', 'ita',
  'jpn', 'kor', 'por', 'zho', 'cmn', 'sin', 'tam', 'hin', 'ben', 'guj', 'pan',
  'urd', 'kan', 'mar', 'tel', 'aze', 'lit', 'yor',
] as const

export type LocaleLoaders = Record<string, LocaleLoader>

let localeLoaders: LocaleLoaders | null = null
let deferredEnglishLoader: (() => Promise<unknown>) | null = null
/** A locale asked for before the loaders arrived; loaded when they do. */
let pendingLocale: string | null = null

// English as the app currently holds it: the boot part, then the whole file
// once loadDeferredEnglish() lands. Reactive, so every t() that fell through
// to a caller's fallback while the deferred part was on its way re-renders
// with the real string.
const english = shallowRef<Record<string, any>>(engBoot)

// Cache resolved locales so re-selecting one doesn't refetch.
const loadedLocales: Record<string, Record<string, any>> = { eng: engBoot }

let deferredEnglish: Promise<void> | null = null
/**
 * Fetch the rest of English — the dashboard, org, teacher and insight
 * namespaces (job #850). Idempotent; a failed fetch is forgotten so the next
 * caller retries. Never rejects: a caller awaiting it (the router) must not be
 * stopped by it, and a missing string already degrades to its fallback.
 */
export function loadDeferredEnglish(): Promise<void> {
  const loader = deferredEnglishLoader
  if (!loader) return Promise.resolve()
  deferredEnglish ??= Promise.resolve().then(() => loader())
    .then((m) => {
      const merged = { ...english.value, ...unwrap(m) }
      english.value = merged
      loadedLocales.eng = merged
      if (currentLocale.value === 'eng') currentMessages.value = merged
    })
    .catch((err) => {
      deferredEnglish = null
      console.warn('[useI18n] Failed to load the deferred English strings', err)
    })
  return deferredEnglish
}

// localStorage key for persisting locale preference
const LOCALE_STORAGE_KEY = 'ssi-locale'

/**
 * Where the stored locale came from: a person picked it, or we guessed.
 *
 * A deep link to a course INFERS an interface language from that course's
 * known language (utils/deepLinkLocale.ts) — a good guess, because clicking
 * "Spanish for Hindi speakers" says more about what you read than a browser
 * header does. But a guess must never overwrite a choice, so the two have to
 * be told apart, and one string can't do that.
 *
 * A stored locale with NO source key is treated as CHOSEN. Until this key
 * existed the only writer was the Settings language picker, so every legacy
 * value in the wild is a deliberate pick and inherits the stronger status.
 */
const LOCALE_SOURCE_KEY = 'ssi-locale-source'

export type LocaleSource = 'chosen' | 'inferred'

// Load the saved locale or default to English. Only a CHOSEN locale is ever
// honoured on boot (Aran, 2026-10-30): an 'inferred' value written by an
// earlier build's deep-link guess is a stale guess, not a choice, so it is
// dropped here rather than carried into every later visit.
const getSavedLocale = (): string => {
  try {
    const stored = localStorage.getItem(LOCALE_STORAGE_KEY)
    if (!stored) return 'eng'
    if (localStorage.getItem(LOCALE_SOURCE_KEY) === 'inferred') {
      localStorage.removeItem(LOCALE_STORAGE_KEY)
      localStorage.removeItem(LOCALE_SOURCE_KEY)
      return 'eng'
    }
    return stored
  } catch {
    return 'eng'
  }
}

/**
 * True when the current interface language is a person's own decision, and
 * therefore off-limits to inference. False when nothing is stored at all, or
 * when what's stored was itself inferred by an earlier deep link.
 */
export const hasChosenLocale = (): boolean => {
  try {
    if (!localStorage.getItem(LOCALE_STORAGE_KEY)) return false
    return localStorage.getItem(LOCALE_SOURCE_KEY) !== 'inferred'
  } catch {
    // No storage — nothing can have been chosen, so nothing is protected.
    return false
  }
}

/**
 * Drop an in-memory inferred interface language back to English. The router
 * calls it on schools, admin and onboarding routes, where the interface is
 * English unless the person chose otherwise. A chosen locale is untouched.
 */
export function dropInferredLocale(): void {
  if (currentSource !== 'inferred' || currentLocale.value === 'eng') return
  void setLocale('eng', 'inferred', { persist: false })
}

/** True when ANY locale is stored, chosen or inferred. Nothing stored = a first visit. */
export const hasStoredLocale = (): boolean => {
  try {
    return !!localStorage.getItem(LOCALE_STORAGE_KEY)
  } catch {
    return false
  }
}

// Current locale state (shared across app).
// We start with English messages even if the saved locale is something
// else — the real locale loads asynchronously below. Strings render in
// English for the brief window before the chunk arrives, which is the
// same fallback behaviour t() already has for any missing key.
const savedLocale = getSavedLocale()
let currentSource: LocaleSource = 'chosen'
const currentLocale: Ref<string> = ref(savedLocale)
const currentMessages: Ref<Record<string, any>> = ref(engBoot)

/**
 * Is there a translated interface for this language code?
 *
 * The answer is exactly "do we ship a locale chunk for it", which is what
 * LOCALE_CODES lists — so inference asks this rather than keeping a
 * second list that could drift from the files on disk.
 */
export const isSupportedLocale = (langCode: string | null | undefined): boolean =>
  !!langCode && (LOCALE_CODES as readonly string[]).includes(langCode)

/**
 * The code a language picker lists for the locale on screen.
 *
 * Several locale codes share one locale file: the Welsh dialects `cym_n` and
 * `cym_s` render cym.json, `ga` renders gle.json, `cmn` renders zho.json. A
 * deep link to `eng_for_cym_n` sets `cym_n`, and a picker that only lists
 * `cym` must still say Cymraeg rather than fall back to English. Pickers read
 * this off the LIVE locale, never off localStorage: the phone-language guess
 * is applied in memory only, so storage can say nothing while the screen is
 * Welsh (Aran, 2026-10-17: "Settings says English, most of it is Welsh").
 */
const PICKER_CODE: Record<string, string> = { cym_n: 'cym', cym_s: 'cym', ga: 'gle', cmn: 'zho' }
export const interfaceLanguageCode = (langCode: string): string => PICKER_CODE[langCode] ?? langCode

/**
 * Set the current locale. Persists to localStorage and fetches the
 * locale chunk on demand if it isn't already loaded. Async because
 * non-English locales are dynamic imports; existing callers don't
 * await the promise (fire-and-forget is fine — currentMessages is
 * reactive so the UI re-renders when the chunk lands).
 */
export const setLocale = async (
  langCode: string,
  source: LocaleSource = 'chosen',
  /**
   * Write the choice down? Defaults to yes, which is every existing call site
   * — a person tapping a language, and the deep-link inference on the ordinary
   * app. The framed marketing demo passes false: it renders in the course's
   * known language like everything else, but it is a page inside somebody
   * else's site and it stores NOTHING. In-memory locale, gone with the frame.
   */
  { persist = true }: { persist?: boolean } = {},
): Promise<void> => {
  if (!isSupportedLocale(langCode)) return // unknown locale — keep current

  // Persist the choice immediately so a quick refresh keeps the
  // preference even if the chunk download hasn't completed yet.
  // `source` defaults to 'chosen' so every existing call site — all of
  // which are a person tapping a language — keeps its stronger status
  // without being touched.
  if (persist) {
    try {
      localStorage.setItem(LOCALE_STORAGE_KEY, langCode)
      localStorage.setItem(LOCALE_SOURCE_KEY, source)
    } catch {
      // localStorage might be unavailable
    }
  }
  currentLocale.value = langCode
  currentSource = source
  syncDocumentLang()

  // Use cached messages if we've already loaded this locale.
  const cached = loadedLocales[langCode]
  if (cached) {
    currentMessages.value = cached
    return
  }

  // The previous locale's copy must not survive a pending or failed switch.
  currentMessages.value = english.value

  const loader = localeLoaders?.[langCode]
  if (!loader) {
    // Before the entry has registered the loaders: registerLocaleLoaders
    // loads whichever locale was asked for last.
    pendingLocale = langCode
    return
  }
  // Interface switches and background translations share the same pending load.
  await loadLocaleFor(langCode)
  const messages = loadedLocales[langCode]
  // A failed load remains retryable; a later switch keeps its own messages.
  if (messages && currentLocale.value === langCode) {
    currentMessages.value = messages
  }
}

/**
 * Walk a dot-path and return the leaf only if it is usable copy.
 *
 * "Usable" excludes three things that all used to reach the screen:
 *   - a missing path (the obvious case);
 *   - a leaf that is an object, not a string — a locale file whose nesting
 *     has drifted from English's;
 *   - an empty or whitespace-only string, which is a translator's blank
 *     rather than a translation and renders as a bald gap in the UI.
 * Returning undefined for all three lets t() try English next.
 */
const resolveMessage = (source: any, parts: string[]): string | undefined => {
  let value: any = source
  for (const part of parts) {
    if (value && typeof value === 'object' && part in value) {
      value = value[part]
    } else {
      return undefined
    }
  }
  return typeof value === 'string' && value.trim() !== '' ? value : undefined
}

// Render passes repeat the same paths. Cache only their segments, never the
// resolved copy: locale changes and deferred messages must stay reactive.
// Bound the cache because callers can also supply dynamically generated keys.
const messagePaths = new Map<string, string[]>()
const MAX_MESSAGE_PATHS = 1024
function messagePath(key: string): string[] {
  let parts = messagePaths.get(key)
  if (!parts) {
    parts = key.split('.')
    if (messagePaths.size >= MAX_MESSAGE_PATHS) {
      messagePaths.delete(messagePaths.keys().next().value!)
    }
    messagePaths.set(key, parts)
  }
  return parts
}

/**
 * Get a translation by key path (e.g., 'courseSelector.title').
 *
 * Current locale → English → the caller's fallback → the key itself. Every
 * step down is silent by design: a half-translated locale must render an
 * English word, never a blank or a raw `settings.terms`.
 */
export const t = (key: string, fallback?: string): string => {
  const parts = messagePath(key)
  return resolveMessage(currentMessages.value, parts)
    ?? resolveMessage(english.value, parts)
    ?? fallback
    ?? key
}

/**
 * English only, whatever the interface locale. For the games (/guess, /listen,
 * /pin, /words*): their audience is learners of any course, so the chrome is
 * always English (Aran, 2026-10-10) and never follows the interface language.
 */
export const tEn = (key: string, fallback?: string): string =>
  resolveMessage(english.value, messagePath(key)) ?? fallback ?? key

/**
 * The interface language only when a person CHOSE it; English otherwise. For the
 * signup doors (/schools1, /tutors, /orgs, job #153): a teacher whose browser
 * is set to Welsh got a page half in Welsh, because the phone-language guess
 * (deepLinkLocale.applyBootLocale) and a course deep link both set the locale
 * without anyone picking it, while most of the page is English copy. Interface
 * text there is English unless the visitor has selected another language (Aran,
 * 2026-10-30). A choice made in Settings is honoured exactly as t() honours it.
 */
export const tChosen = (key: string, fallback?: string): string =>
  (hasChosenLocale() ? t(key, fallback) : tEn(key, fallback))

/**
 * One named locale, whatever the interface locale, and without changing it: for the English
 * editions of the word game (job #856), whose chrome is in the player's own language because
 * the game measures their ENGLISH. Nothing is stored. Until the locale's chunk has landed (call
 * loadLocaleFor first) this falls through to English, then the fallback, as t() does.
 */
const localeTick = ref(0)
export const tIn = (langCode: string, key: string, fallback?: string): string => {
  void localeTick.value
  const parts = messagePath(key)
  return resolveMessage(loadedLocales[langCode], parts) ?? resolveMessage(english.value, parts) ?? fallback ?? key
}

const pendingLocaleLoads = new Map<string, Promise<void>>()
/** Load one locale's chunk for tIn, leaving the interface locale alone. Resolves when it is there (or failed). */
export async function loadLocaleFor(langCode: string): Promise<void> {
  if (!isSupportedLocale(langCode) || loadedLocales[langCode]) return
  const pending = pendingLocaleLoads.get(langCode)
  if (pending) return pending
  const loader = localeLoaders?.[langCode]
  if (!loader) return
  // Coalesce callers before invoking the loader; publish one reactive update.
  const request = Promise.resolve().then(loader).then((messages) => {
    loadedLocales[langCode] = messages
    localeTick.value++
  }).catch((err) => {
    console.warn('[useI18n] Failed to load locale', langCode, err)
  }).finally(() => {
    pendingLocaleLoads.delete(langCode)
  })
  pendingLocaleLoads.set(langCode, request)
  return request
}

const PLURAL_CATEGORIES = ['zero', 'one', 'two', 'few', 'many', 'other']

/**
 * Split a plural message into its forms. A plural message is one string of
 * forms separated by " | ", each prefixed with its CLDR category:
 * "one=1 class | other={n} classes". A string with no "category=" prefix is a
 * single form that serves every count, which is right for languages with no
 * grammatical plural (Chinese, Japanese, Korean...).
 */
export const parsePluralForms = (message: string): Record<string, string> => {
  const forms: Record<string, string> = {}
  for (const part of message.split(' | ')) {
    const m = /^(zero|one|two|few|many|other)=([\s\S]*)$/.exec(part)
    if (m) forms[m[1]] = m[2]
    else forms.other = part
  }
  return forms
}

// Cache the grammar, not the rendered count or locale-dependent category.
// Message text is the key so changed locales and fallback copy take effect
// immediately. Keep this private: parsePluralForms callers may mutate results.
const pluralFormsCache = new Map<string, Record<string, string>>()
const MAX_PLURAL_MESSAGES = 1024
function cachedPluralForms(message: string): Record<string, string> {
  let forms = pluralFormsCache.get(message)
  if (!forms) {
    forms = parsePluralForms(message)
    if (pluralFormsCache.size >= MAX_PLURAL_MESSAGES) {
      pluralFormsCache.delete(pluralFormsCache.keys().next().value!)
    }
    pluralFormsCache.set(message, forms)
  }
  return forms
}

const pluralRulesCache = new Map<string, Intl.PluralRules>()

/** The CLDR plural category of `count` in the current interface language. */
export const pluralCategory = (count: number, langCode: string = currentLocale.value): string => {
  const code = interfaceLanguageCode(langCode)
  const tag = ISO3_TO_BCP47[code] || code
  try {
    let rules = pluralRulesCache.get(tag)
    if (!rules) { rules = new Intl.PluralRules(tag); pluralRulesCache.set(tag, rules) }
    return rules.select(count)
  } catch {
    return count === 1 ? 'one' : 'other'
  }
}

/**
 * Translate a message whose wording depends on a count, in the grammar of the
 * interface language (Welsh has six plural categories, Arabic six, Irish five).
 * The locale string carries one form per category it needs; `{n}` is replaced
 * with the count, and any other `{name}` from `vars`. A missing category falls
 * back to the `other` form, so an English fallback served under a language with
 * more categories still reads correctly.
 */
export const tPlural = (key: string, count: number, fallback?: string, vars: Record<string, string | number> = {}): string => {
  const forms = cachedPluralForms(t(key, fallback))
  const form = forms[pluralCategory(count)]
    ?? forms.other
    ?? forms[PLURAL_CATEGORIES.find((c) => c in forms) as string]
    ?? key
  let out = form.split('{n}').join(String(count))
  for (const [name, value] of Object.entries(vars)) out = out.split(`{${name}}`).join(String(value))
  return out
}

/**
 * ISO 639-3 (3-char) → ISO 639-1 (2-char) mapping for Intl.DisplayNames.
 * Only needed where the codes differ. Most 2-char codes are just the first 2 chars.
 */
export const ISO3_TO_BCP47: Record<string, string> = {
  eng: 'en', spa: 'es', fra: 'fr', deu: 'de', ita: 'it', por: 'pt',
  nld: 'nl', pol: 'pl', rus: 'ru', cym: 'cy', gle: 'ga', gla: 'gd',
  jpn: 'ja', zho: 'zh', cmn: 'zh', kor: 'ko', ara: 'ar', hin: 'hi',
  tur: 'tr', swa: 'sw', ron: 'ro', cat: 'ca', eus: 'eu', glg: 'gl',
  swe: 'sv', nor: 'no', nob: 'nb', nno: 'nn', dan: 'da', fin: 'fi',
  isl: 'is', hrv: 'hr', srp: 'sr', bos: 'bs', slv: 'sl', ces: 'cs',
  slk: 'sk', ukr: 'uk', bul: 'bg', mkd: 'mk', ell: 'el', hun: 'hu',
  heb: 'he', sqi: 'sq', lit: 'lt', lav: 'lv', est: 'et', tha: 'th',
  vie: 'vi', ind: 'id', fil: 'tl', ben: 'bn', urd: 'ur', tam: 'ta',
  tel: 'te', msa: 'ms', yue: 'yue', fas: 'fa', kur: 'ku', amh: 'am',
  hau: 'ha', yor: 'yo', zul: 'zu', kat: 'ka', hye: 'hy', bre: 'br',
  cor: 'kw', sin: 'si', kan: 'kn', mar: 'mr', guj: 'gu', pan: 'pa',
}

/**
 * Publish the interface language on `<html lang>`.
 *
 * `index.html` ships `lang="en"` and nothing ever moved it, so a Hindi or
 * Yoruba interface still claimed to be English. Two things read this:
 *   - the glyph-coverage CSS (styles/design-tokens.css) — DM Sans cannot spell
 *     Devanagari or the Yoruba dot-belows, and `:lang()` is how those UI
 *     strings reach Noto Sans as a whole run rather than character by
 *     character;
 *   - screen readers and hyphenation, which were being told the wrong thing.
 *
 * BCP 47 where we have the 2-letter code, otherwise the app's own 639-3 code;
 * the CSS matches both forms.
 */
// A `function` declaration, not a `const` arrow, deliberately: setLocale()
// calls this, and the boot-time apply at the foot of the module calls
// setLocale(). A const would sit in its temporal dead zone at that point and
// throw ReferenceError — which is exactly the bug that shipped on 2026-08-18
// (067b8de8) and silently left every non-English learner in English.
export function syncDocumentLang(): void {
  if (typeof document === 'undefined') return
  const code = interfaceLanguageCode(currentLocale.value)
  document.documentElement.lang = ISO3_TO_BCP47[code] || code
}

/**
 * Map ISO 639-3 to BCP 47 for locale parameter (known language → locale)
 */
const ISO3_TO_LOCALE: Record<string, string> = {
  ...ISO3_TO_BCP47,
  // Variant codes map to base locale
  cym_n: 'cy', cym_s: 'cy',
}

// Reuse ICU formatters across course-label filtering, sorting and rendering.
// Keep polyfill constructors separate and bound each locale cache because
// overrideLangCode can also come from course catalogue data.
const languageDisplayNames = new WeakMap<typeof Intl.DisplayNames, Map<string, Intl.DisplayNames>>()
// Cache only ICU results, after curated messages have been checked. Scoping to
// the formatter keeps locales and replacement/polyfill constructors isolated.
const languageNameResults = new WeakMap<Intl.DisplayNames, Map<string, string | undefined>>()

function languageNameFor(formatter: Intl.DisplayNames, code: string): string | undefined {
  let names = languageNameResults.get(formatter)
  if (!names) {
    names = new Map()
    languageNameResults.set(formatter, names)
  }
  if (names.has(code)) return names.get(code)
  const name = formatter.of(code) // Failed lookups remain retryable.
  if (names.size >= 256) names.delete(names.keys().next().value!)
  names.set(code, name)
  return name
}

const languageDisplayNamesFor = (locale: string): Intl.DisplayNames => {
  let cache = languageDisplayNames.get(Intl.DisplayNames)
  if (!cache) {
    cache = new Map()
    languageDisplayNames.set(Intl.DisplayNames, cache)
  }
  let formatter = cache.get(locale)
  if (!formatter) {
    formatter = new Intl.DisplayNames([locale], { type: 'language' })
    if (cache.size >= 32) cache.delete(cache.keys().next().value!)
    cache.set(locale, formatter)
  }
  return formatter
}

/**
 * Get a language name in the reading language.
 *
 * Our own locale JSON wins, then Intl.DisplayNames, then the raw code.
 *
 * The JSON has to come first. Intl.DisplayNames does NOT report failure:
 * on a device whose ICU carries no display-name data for the interface
 * language it silently answers in ENGLISH, and an English answer is
 * indistinguishable from a correct one ("Icelandic" is not the code "is",
 * so the old `name !== bcp47` guard accepted it). That is how a Welsh
 * interface produced "Islandeg i siaradwyr English" — the sentence frame
 * comes from cym.json and is always Welsh, while the language name came
 * from Intl and could quietly fall back to English. Our JSON carries
 * curated names for every language we ship, so consulting it first makes
 * the name as device-independent as the sentence around it.
 *
 * By default renders in the current UI locale. Pass `overrideLangCode`
 * (ISO 639-3) to render the name in a specific language instead — used
 * by the Premium-courses list so each card reads in the perspective
 * of the learner who would take that course (e.g. "Inglés" instead of
 * "English" on the Spanish-speaker card).
 */
export const getLanguageName = (langCode: string | null | undefined, overrideLangCode?: string): string => {
  // A display helper must never throw: a course row with no target_lang
  // (e.g. a class pointing at a course code the catalogue can't resolve)
  // crashed the whole player render through this line (2026-07-16).
  if (!langCode) return ''

  // 1. Our own curated names, in the reading language. For an override we
  //    can only use the JSON if that locale's chunk is already loaded —
  //    this helper is synchronous, so an unloaded override falls to Intl.
  const messages = overrideLangCode
    ? loadedLocales[overrideLangCode]
    : currentMessages.value
  const curated = messages?.languages?.[langCode]
  // Dialect keys (cym_n, nob…) carry their full name in the JSON, suffix
  // included — so a hit is returned exactly as authored.
  if (typeof curated === 'string' && curated) return curated

  // 2. Intl.DisplayNames — browser-native, covers anything not in the JSON.
  // Use explicit mapping if available, otherwise try the raw code (works for many ISO 639-3 codes)
  const bcp47 = ISO3_TO_BCP47[langCode] || langCode
  const localeCode = overrideLangCode
    ? (ISO3_TO_LOCALE[overrideLangCode] || overrideLangCode)
    : (ISO3_TO_LOCALE[currentLocale.value] || 'en')
  try {
    const displayNames = languageDisplayNamesFor(localeCode)
    const name = languageNameFor(displayNames, bcp47)
    if (name && name !== bcp47) {
      // Capitalize first letter (some locales return lowercase)
      const capitalized = name.charAt(0).toUpperCase() + name.slice(1)
      // Append variant suffix for dialect codes like cym_n
      if (langCode === 'cym_n') return `${capitalized} (${t('languages.cym_n_suffix', 'North')})`
      if (langCode === 'cym_s') return `${capitalized} (${t('languages.cym_s_suffix', 'South')})`
      if (langCode === 'nob') return `${capitalized} (Bokmål)`
      if (langCode === 'nno') return `${capitalized} (Nynorsk)`
      return capitalized
    }
  } catch {
    // Intl.DisplayNames not supported or code unknown — fall through
  }

  // 3. An override must not fall back to an unrelated interface language.
  // Without an override, retain the interface locale's normal fallback.
  const fallback = overrideLangCode ? tEn : t
  return fallback(`languages.${langCode}`, langCode.toUpperCase())
}

/**
 * French elision: `de` + a vowel-initial name contracts to `d'`.
 *
 * "pour les locuteurs de Anglais" is not a sentence a French speaker would
 * write — it reads "de Anglais" where the language demands "d'Anglais".
 * The template can't do this itself: whether it elides depends on the name
 * that lands in the slot, which is chosen at runtime.
 */
// Compile once: browsing courses composes these subtitles on every render.
// No global flag, so reusing a pattern cannot carry lastIndex between labels.
const FRENCH_ELIDABLE = [
  [/(^|\s)de \{lang\}/, "d'"],
  [/(^|\s)le \{lang\}/, "l'"],
  [/(^|\s)la \{lang\}/, "l'"],
  [/(^|\s)que \{lang\}/, "qu'"],
] as const
const FRENCH_VOWEL_INITIAL = /^[aeiouyàâäéèêëîïíôöòûüùœæ]/i

/**
 * h aspiré blocks elision ("le hongrois"), h muet does not ("l'hébreu") —
 * and nothing about the spelling says which: it is a per-word property of
 * French, so it has to be listed. Only the h-initial language names we ship
 * need a ruling; anything unlisted is treated as aspiré, which leaves the
 * name untouched rather than mangling it.
 */
const FRENCH_MUTE_H = new Set([
  'heb', // hébreu — l'hébreu
  'hin', // hindi — l'hindi
  // Aspiré, deliberately absent: hau (le haoussa), hun (le hongrois).
])

const elidesInFrench = (langCode: string, langName: string): boolean => {
  if (FRENCH_VOWEL_INITIAL.test(langName)) return true
  return /^h/i.test(langName) && FRENCH_MUTE_H.has(langCode)
}

/**
 * Put a language name into a `{lang}` template, honouring the interface
 * language's own rules for composing it.
 *
 * Only French needs a rule today. Spanish/Portuguese "de {lang}" never
 * elides, German compounds with a hyphen, and the rest place the name
 * without a preposition — so everything else is a plain substitution, and
 * the rule is keyed on the interface locale so an English fallback string
 * can never be French-ified.
 */
export const interpolateLanguageName = (
  template: string,
  langCode: string,
  langName: string,
): string => {
  if (currentLocale.value === 'fra' && elidesInFrench(langCode, langName)) {
    for (const [pattern, contraction] of FRENCH_ELIDABLE) {
      const elided = template.replace(
        pattern,
        `$1${contraction}{lang}`,
      )
      if (elided !== template) return elided.replace('{lang}', langName)
    }
  }
  return template.replace('{lang}', langName)
}

/**
 * The "for X speakers" course subtitle, fully composed: the name in the
 * reading language, inside the reading language's own frame.
 */
export const forSpeakersLabel = (knownLangCode: string | null | undefined): string => {
  if (!knownLangCode) return ''
  const knownName = getLanguageName(knownLangCode)
  const template = t('courseSelector.forSpeakers', 'for {lang} Speakers')
  return interpolateLanguageName(template, knownLangCode, knownName)
}

/**
 * Language endonyms — the name each language calls itself.
 * Used for "I speak" buttons so they're always in the speaker's own language.
 */
const LANGUAGE_ENDONYMS: Record<string, string> = {
  eng: 'English',
  spa: 'Español',
  fra: 'Français',
  deu: 'Deutsch',
  ita: 'Italiano',
  por: 'Português',
  nld: 'Nederlands',
  pol: 'Polski',
  rus: 'Русский',
  cym: 'Cymraeg',
  cym_n: 'Cymraeg (Gogledd)',
  cym_s: 'Cymraeg (De)',
  gle: 'Gaeilge',
  gla: 'Gàidhlig',
  jpn: '日本語',
  zho: '中文',
  cmn: '普通话',
  kor: '한국어',
  ara: 'العربية',
  hin: 'हिन्दी',
  tur: 'Türkçe',
  swa: 'Kiswahili',
  ron: 'Română',
  cat: 'Català',
  eus: 'Euskara',
  glg: 'Galego',
  swe: 'Svenska',
  nor: 'Norsk',
  nob: 'Norsk (Bokmål)',
  nno: 'Norsk (Nynorsk)',
  dan: 'Dansk',
  fin: 'Suomi',
  isl: 'Íslenska',
  hrv: 'Hrvatski',
  srp: 'Српски',
  bos: 'Bosanski',
  slv: 'Slovenščina',
  ces: 'Čeština',
  slk: 'Slovenčina',
  ukr: 'Українська',
  bul: 'Български',
  mkd: 'Македонски',
  ell: 'Ελληνικά',
  hun: 'Magyar',
  heb: 'עברית',
  sqi: 'Shqip',
  lit: 'Lietuvių',
  lav: 'Latviešu',
  est: 'Eesti',
  tha: 'ไทย',
  vie: 'Tiếng Việt',
  ind: 'Bahasa Indonesia',
  fil: 'Filipino',
  ben: 'বাংলা',
  urd: 'اردو',
  tam: 'தமிழ்',
  tel: 'తెలుగు',
  msa: 'Bahasa Melayu',
  yue: '廣東話',
  fas: 'فارسی',
  kur: 'Kurdî',
  amh: 'አማርኛ',
  hau: 'Hausa',
  yor: 'Yorùbá',
  zul: 'isiZulu',
  kat: 'ქართული',
  hye: 'Հայերեն',
  bre: 'Brezhoneg',
  cor: 'Kernewek',
  glv: 'Gaelg',
  sin: 'සිංහල',
  nep: 'नेपाली',
  afr: 'Afrikaans',
}

export const getLanguageEndonym = (langCode: string): string => {
  if (LANGUAGE_ENDONYMS[langCode]) return LANGUAGE_ENDONYMS[langCode]

  // Try Intl.DisplayNames with the language's OWN locale (endonym = self-name)
  const bcp47 = ISO3_TO_BCP47[langCode] || langCode
  try {
    const displayNames = languageDisplayNamesFor(bcp47)
    const name = languageNameFor(displayNames, bcp47)
    if (name && name !== bcp47) {
      return name.charAt(0).toUpperCase() + name.slice(1)
    }
  } catch {
    // Unknown code — fall through
  }

  return getLanguageName(langCode)
}

/**
 * Language flag emoji lookup (ISO 639-3 → flag)
 * Comprehensive: add new languages here once — CourseSelector and LearningPlayer both use this.
 */
const LANGUAGE_FLAGS: Record<string, string> = {
  // Celtic
  eng: '🏴󠁧󠁢󠁥󠁮󠁧󠁿',
  cym: '🏴󠁧󠁢󠁷󠁬󠁳󠁿',
  cym_n: '🏴󠁧󠁢󠁷󠁬󠁳󠁿',
  cym_s: '🏴󠁧󠁢󠁷󠁬󠁳󠁿',
  gle: '🇮🇪',
  gla: '🏴󠁧󠁢󠁳󠁣󠁴󠁿',
  bre: '🏴',  // Breton (Gwenn-ha-du — no emoji; use LanguageFlag SVG for proper flag)
  cor: '🏴',  // Cornish (St Piran's — no emoji; use LanguageFlag SVG for proper flag)
  glv: '🇮🇲',  // Manx
  // Romance
  spa: '🇪🇸',
  fra: '🇫🇷',
  ita: '🇮🇹',
  por: '🇵🇹',
  ron: '🇷🇴',  // Romanian
  cat: '🏴',  // Catalan (Senyera — no emoji; use LanguageFlag SVG for proper flag)
  eus: '🏴',  // Basque (Ikurriña — no emoji; use LanguageFlag SVG for proper flag)
  glg: '🏴',  // Galician (no emoji; use LanguageFlag SVG for proper flag)
  // Germanic
  deu: '🇩🇪',
  nld: '🇳🇱',
  swe: '🇸🇪',
  nor: '🇳🇴',
  nob: '🇳🇴',  // Norwegian Bokmål
  nno: '🇳🇴',  // Norwegian Nynorsk
  dan: '🇩🇰',
  fin: '🇫🇮',
  isl: '🇮🇸',  // Icelandic
  // Slavic
  rus: '🇷🇺',
  pol: '🇵🇱',
  ces: '🇨🇿',  // Czech
  slk: '🇸🇰',  // Slovak
  hrv: '🇭🇷',  // Croatian
  srp: '🇷🇸',  // Serbian
  bos: '🇧🇦',  // Bosnian
  slv: '🇸🇮',  // Slovenian
  ukr: '🇺🇦',  // Ukrainian
  bul: '🇧🇬',  // Bulgarian
  mkd: '🇲🇰',  // Macedonian
  // East Asian
  jpn: '🇯🇵',
  kor: '🇰🇷',
  cmn: '🇨🇳',
  zho: '🇨🇳',
  yue: '🇭🇰',  // Cantonese
  // South/Southeast Asian
  hin: '🇮🇳',  // Hindi
  ben: '🇧🇩',  // Bengali
  urd: '🇵🇰',  // Urdu
  tam: '🇮🇳',  // Tamil
  tel: '🇮🇳',  // Telugu
  tha: '🇹🇭',  // Thai
  vie: '🇻🇳',  // Vietnamese
  msa: '🇲🇾',  // Malay
  ind: '🇮🇩',  // Indonesian
  fil: '🇵🇭',  // Filipino
  sin: '🇱🇰',  // Sinhala
  nep: '🇳🇵',  // Nepali
  // Semitic & Middle Eastern
  ara: '🇸🇦',
  heb: '🇮🇱',
  tur: '🇹🇷',
  fas: '🇮🇷',  // Persian/Farsi
  kur: '🇮🇶',  // Kurdish
  // African
  swa: '🇰🇪',  // Swahili
  amh: '🇪🇹',  // Amharic
  hau: '🇳🇬',  // Hausa
  yor: '🇳🇬',  // Yoruba
  zul: '🇿🇦',  // Zulu
  afr: '🇿🇦',  // Afrikaans
  // Other European
  ell: '🇬🇷',  // Greek
  kat: '🇬🇪',  // Georgian
  hye: '🇦🇲',  // Armenian
  lit: '🇱🇹',  // Lithuanian
  lav: '🇱🇻',  // Latvian
  est: '🇪🇪',  // Estonian
  hun: '🇭🇺',  // Hungarian
  sqi: '🇦🇱',  // Albanian
}

/**
 * Auto-generate flag emoji from ISO 639-3 → ISO 3166-1 alpha-2 country code.
 * Falls back to 🌐 if no mapping exists.
 */
const LANG_TO_COUNTRY: Record<string, string> = {
  // Only needed for languages NOT in LANGUAGE_FLAGS above
  // This is a safety net for new courses
}

function countryCodeToFlag(cc: string): string {
  const upper = cc.toUpperCase()
  if (upper.length !== 2) return '🌐'
  return String.fromCodePoint(
    0x1F1E6 + upper.charCodeAt(0) - 65,
    0x1F1E6 + upper.charCodeAt(1) - 65
  )
}

export const getLanguageFlag = (langCode: string): string => {
  if (LANGUAGE_FLAGS[langCode]) return LANGUAGE_FLAGS[langCode]
  // A variant course flies its own country's flag, resolved by the same rule
  // the SVG flags use — so a new variant needs no entry here either.
  const variantCc = variantCountryCode(langCode)
  if (variantCc) return countryCodeToFlag(variantCc)
  const cc = LANG_TO_COUNTRY[langCode]
  if (cc) return countryCodeToFlag(cc)
  // Fall back to the parent language of a variant we can't place.
  const base = extractBaseLanguage(langCode)
  if (base !== langCode && LANGUAGE_FLAGS[base]) return LANGUAGE_FLAGS[base]
  return '🌐'
}

// Boot: publish the saved locale on <html lang> straight away. setLocale() keeps
// it in step from here on; this runs at the foot of the module because it reads
// the ISO3_TO_BCP47 table defined above it.
syncDocumentLang()

// Boot: APPLY the saved locale — i.e. fetch its messages — not just publish it.
// Storing the choice was never the broken half; applying it on the next launch
// was (tester report, 2026-08-25: "setting says Cymraeg, everything reads
// English"). Runs at the foot of the module, after every declaration it can
// reach, so nothing here can hit a temporal dead zone again. Fire-and-forget:
// the UI mounts in English and rerenders when the locale chunk lands
// (~50-200ms). A failure is LOUD — a silent catch is what hid this for a week.
//
// Nothing is re-saved on boot, so an inferred guess is never promoted to a
// choice by it. (Caught on staging 2026-09-02: a re-save that took setLocale's
// 'chosen' default re-stamped a deep link's Hindi guess, and a second link,
// to a Tamil-known course, was then refused.)
//
// Since job #966 the loaders arrive from the entry, which evaluates AFTER this
// module, so the apply runs inside registerLocaleLoaders below.
/**
 * Hand in the lazy locale loaders. Called once, by src/i18n/localeLoaders.ts,
 * which the entry imports. Applies the saved locale, then loads whichever
 * locale was asked for in the meantime (a deep link can ask first).
 */
export function registerLocaleLoaders(loaders: LocaleLoaders, deferredEnglishChunk: () => Promise<unknown>): void {
  localeLoaders = loaders
  deferredEnglishLoader = deferredEnglishChunk
  // The locale on screen is the saved one, or whatever a deep link asked for
  // before now; either way currentLocale already names it and only its
  // messages are missing. Nothing is re-saved: it was stored when it was set.
  const want = pendingLocale ?? savedLocale
  pendingLocale = null
  if (want !== 'eng' && want === currentLocale.value && isSupportedLocale(want) && !loadedLocales[want]) {
    setLocale(want, hasChosenLocale() ? 'chosen' : 'inferred', { persist: false }).catch((err) => {
      console.warn('[useI18n] Failed to apply saved locale on boot', want, err)
    })
  }
}

/**
 * Composable for use in Vue components
 */
export function useI18n() {
  return {
    locale: computed(() => currentLocale.value),
    setLocale,
    t,
    tEn,
    tChosen,
    tPlural,
    getLanguageName,
    // Direct access to messages for v-for scenarios
    messages: computed(() => currentMessages.value),
  }
}

export default useI18n
